import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import InstallGate from "../components/InstallGate";
import Logo from "../components/Logo";
import InstallCTA from "../components/InstallCTA";
import { Check, AlertTriangle, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { createContactInviteFlow } from "../lib/contactInviteFlow.mjs";

export default function JoinInvite() {
  const { token } = useParams();
  return (
    <InstallGate>
      <JoinInviteContent key={token} />
    </InstallGate>
  );
}

function JoinInviteContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [{ invite, error: err, busy }, setState] = useState({ invite: null, error: null, busy: null });
  const accepting = busy === "accept";
  const declining = busy === "decline";
  const authRef = useRef({ user, loading });
  authRef.current = { user, loading };
  const flowRef = useRef(null);

  useEffect(() => {
    const flow = createContactInviteFlow({
      token, api, getAuth: () => authRef.current, navigate,
      storage: {
        getItem: (key) => localStorage.getItem(key),
        setItem: (key, value) => localStorage.setItem(key, value),
        removeItem: (key) => localStorage.removeItem(key),
      },
      notify: toast, onChange: setState,
    });
    flowRef.current = flow;
    flow.load();
    return () => { flow.dispose(); flowRef.current = null; };
  }, [token, navigate]);

  const goHome = () => flowRef.current?.leave();
  const accept = () => flowRef.current?.accept();
  const decline = () => flowRef.current?.decline();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex flex-col">
      <header className="px-6 pt-7 flex items-center justify-between">
        {/* Always-visible back button — even before the invite has loaded,
            even if the fetch is hung, users can leave. */}
        <button
          data-testid="join-back-btn"
          onClick={goHome}
          className="size-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
          aria-label="Back"
        >
          <ArrowLeft size={16}/>
        </button>
        <Logo size={44} withWordmark/>
        <span className="size-9" aria-hidden="true"/>
      </header>

      <main className="flex-1 grid place-items-center px-6">
        <div className="w-full max-w-md">
          {err && (
            <div className="text-center" data-testid="join-error-state">
              <AlertTriangle className="mx-auto text-[#FF3B30]" size={32}/>
              <div className="font-display font-bold text-xl mt-4">Invite unavailable</div>
              <div className="text-zinc-500 mt-2 text-sm">{err}</div>
              <button
                data-testid="join-error-home-btn"
                onClick={goHome}
                className="mt-6 font-bold text-[#007AFF]"
              >
                Go home →
              </button>
            </div>
          )}

          {!err && invite && (
            <>
              <div className="label-eyebrow text-center">You've been invited</div>
              <div className="flex flex-col items-center mt-5">
                <div className="size-20 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-black text-2xl ring-4 ring-[#007AFF]/20">
                  {invite.owner_picture
                    ? <img src={invite.owner_picture} alt="" className="w-full h-full object-cover"/>
                    : (invite.owner_name || "?").slice(0,1)}
                </div>
                <div className="font-display font-black text-3xl tracking-tighter mt-5 text-center">
                  {invite.owner_name}
                </div>
                <div className="text-sm text-zinc-500 mt-1">{invite.owner_email}</div>
                <div className="mt-6 px-4 py-2 rounded-full bg-[#007AFF]/12 border border-[#007AFF]/30 text-sm">
                  wants to {invite.relation === "child" ? "be your parent on" : "share location with you on"} <span className="font-bold">Proximity</span>
                </div>
              </div>

              {/* Slogan + native install button (always visible on invite page) */}
              <div className="mt-8">
                <InstallCTA compact />
              </div>

              {invite.status !== "pending" ? (
                <div className="mt-8 text-center text-zinc-500 text-sm">
                  This invite is already <span className="font-bold uppercase">{invite.status}</span>.
                  <div className="mt-4">
                    <button
                      data-testid="join-nonpending-home-btn"
                      onClick={goHome}
                      className="font-bold text-[#007AFF]"
                    >
                      Go home →
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    data-testid="accept-link-btn"
                    onClick={accept}
                    disabled={accepting || declining || loading}
                    className="mt-10 w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3.5 rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Check size={18}/>
                    {user ? (accepting ? "Connecting…" : "Accept invite") : "Sign in to review invite"}
                  </button>
                  <button
                    data-testid="decline-link-btn"
                    onClick={decline}
                    disabled={accepting || declining}
                    className="mt-3 w-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-200 font-bold py-3 rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <X size={16}/>
                    {declining ? "Declining…" : "Decline"}
                  </button>
                  <div className="text-center text-xs text-zinc-500 mt-4">
                    You'll share your live location only with {invite.owner_name}. You can disconnect anytime.
                  </div>
                  {accepting && (
                    <p className="text-center text-xs text-zinc-500 mt-2">
                      Leaving this screen will not undo a connection already processed by the server.
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {!err && !invite && (
            <div className="text-center" data-testid="join-loading-state">
              <div className="label-eyebrow text-zinc-500">Loading invite…</div>
              {/* Leaving also cancels the lookup and ignores late responses. */}
              <button
                data-testid="join-loading-cancel-btn"
                onClick={goHome}
                className="mt-6 text-sm text-zinc-500 underline"
              >
                Cancel and go home
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
